import { flashcardConstants } from '../../constants/flashcard.constants';

export interface FlashcardFolder {
  id: number;
  name: string;
  description?: string;
  color: string;
  icon: string;
  visibility: 'public' | 'private' | 'class';
  created_by: number;
  sets?: FlashcardSet[];
}

export interface FlashcardSet {
  id: number;
  folder_id?: number;
  name: string;
  description?: string;
  language_from: string;
  language_to: string;
  difficulty: string;
  tags: string[];
  created_by: number;
  total_cards: number;
}

export interface FlashcardCard {
  id: number;
  set_id: number;
  front: string;
  back: string;
  phonetic?: string;
  example?: string;
  note?: string;
  image_url?: string;
  audio_url?: string;
  difficulty: string;
  position: number;
}

interface FlashcardState {
  folders: FlashcardFolder[];
  sets: FlashcardSet[];
  cards: FlashcardCard[];
  selectedFolderId: number | null;
  loading: boolean;
  fileUrl?: string | null;
  error: string | null;
}

const initialState: FlashcardState = {
  folders: [],
  sets: [],
  cards: [],
  selectedFolderId: null,
  loading: false,
  fileUrl: null,
  error: null,
};

export function flashcard(state = initialState, action: any): FlashcardState {
  switch (action.type) {
    // ==================== FOLDERS ====================
    case flashcardConstants.GET_ALL_FOLDERS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.GET_ALL_FOLDERS_SUCCESS:
      return {
        ...state,
        folders: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
      };
    case flashcardConstants.GET_ALL_FOLDERS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.CREATE_FOLDER_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.CREATE_FOLDER_SUCCESS:
      return {
        ...state,
        folders: [...(Array.isArray(state.folders) ? state.folders : []), action.payload],
        loading: false,
      };
    case flashcardConstants.CREATE_FOLDER_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.UPDATE_FOLDER_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.UPDATE_FOLDER_SUCCESS:
      return {
        ...state,
        folders: (Array.isArray(state.folders) ? state.folders : []).map((f) =>
          f.id === action.payload.id ? action.payload : f
        ),
        loading: false,
      };
    case flashcardConstants.UPDATE_FOLDER_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.DELETE_FOLDER_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.DELETE_FOLDER_SUCCESS:
      return {
        ...state,
        folders: (Array.isArray(state.folders) ? state.folders : []).filter((f) => f.id !== action.payload),
        sets: (Array.isArray(state.sets) ? state.sets : []).filter((s) => s.folder_id !== action.payload),
        loading: false,
      };
    case flashcardConstants.DELETE_FOLDER_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // ==================== SETS ====================
    case flashcardConstants.GET_ALL_SETS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.GET_ALL_SETS_SUCCESS:
      return {
        ...state,
        sets: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
      };
    case flashcardConstants.GET_ALL_SETS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.GET_SETS_BY_FOLDER_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.GET_SETS_BY_FOLDER_SUCCESS:
      return {
        ...state,
        sets: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
      };
    case flashcardConstants.GET_SETS_BY_FOLDER_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.CREATE_SET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.CREATE_SET_SUCCESS:
      return {
        ...state,
        sets: [...(Array.isArray(state.sets) ? state.sets : []), action.payload],
        loading: false,
      };
    case flashcardConstants.CREATE_SET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.UPDATE_SET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.UPDATE_SET_SUCCESS:
      return {
        ...state,
        sets: (Array.isArray(state.sets) ? state.sets : []).map((s) =>
          s.id === action.payload.id ? action.payload : s
        ),
        loading: false,
      };
    case flashcardConstants.UPDATE_SET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.DELETE_SET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.DELETE_SET_SUCCESS:
      return {
        ...state,
        sets: (Array.isArray(state.sets) ? state.sets : []).filter((s) => s.id !== action.payload),
        loading: false,
      };
    case flashcardConstants.DELETE_SET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // ==================== CARDS ====================
    case flashcardConstants.CREATE_CARD_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.CREATE_CARD_SUCCESS:
      return {
        ...state,
        cards: [...(Array.isArray(state.cards) ? state.cards : []), action.payload],
        loading: false,
      };
    case flashcardConstants.CREATE_CARD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.GET_CARDS_BY_SET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.GET_CARDS_BY_SET_SUCCESS:
      return {
        ...state,
        cards: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
      };
    case flashcardConstants.GET_CARDS_BY_SET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.BULK_CREATE_CARDS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.BULK_CREATE_CARDS_SUCCESS:
      return {
        ...state,
        cards: [...(Array.isArray(state.cards) ? state.cards : []), ...(Array.isArray(action.payload) ? action.payload : [])],
        loading: false,
      };
    case flashcardConstants.BULK_CREATE_CARDS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case flashcardConstants.DELETE_CARD_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.UPDATE_CARD_SUCCESS:
      return {
        ...state,
        cards: (Array.isArray(state.cards) ? state.cards : []).map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
        loading: false,
      };
    case flashcardConstants.UPDATE_CARD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case flashcardConstants.DELETE_CARD_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case flashcardConstants.DELETE_CARD_SUCCESS:
      return {
        ...state,
        cards: (Array.isArray(state.cards) ? state.cards : []).filter((c) => c.id !== action.payload),
        loading: false,
      };
    case flashcardConstants.DELETE_CARD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // ==================== SELECTION ====================
    case flashcardConstants.SELECT_FOLDER:
      return {
        ...state,
        selectedFolderId: action.payload,
      };

    case flashcardConstants.CLEAR_SELECTION:
      return {
        ...state,
        selectedFolderId: null,
      };

    // Legacy PDF generation state (submitText)
    case 'SUBMIT_REQUEST':
      return { ...state, loading: true, error: null };
    case 'SUBMIT_SUCCESS':
      return { ...state, loading: false, fileUrl: action.payload, error: null };
    case 'SUBMIT_FAILURE':
      return { ...state, loading: false, fileUrl: null, error: action.payload };

    default:
      return state;
  }
}
